import { BordInvoiceInfo } from 'components/BillingDataTab/Components/BordInvoiceInfo/BordInvoiceInfo';
import {
  IInvoiceLogisticServiceRecord,
  IInvoiceOrderRecord,
  IInvoiceRecord,
  TInvoiceSectionVariant,
  INVOICE_SECTION_VARIANT
} from './InvoiceSectionOfBillingTab.types';
import { useEffect, useState } from 'react';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { getInvoiceLogisticServiceRecord, getInvoiceOrderRecord } from 'services/orders';
import InvoiceRow from './InvoiceRow/InvoiceRow';
import { BordOneToneIcon, NudosButton } from 'components/NudosComponents';
import { fgDefault, nudosGray400, nudosRed100 } from 'styles/styles.constants';
import { ICountryDetail } from '../../types/countries';
import InvoiceSectionStorage from './InvoiceSectionStorage/InvoiceSectionStorage';
import './InvoiceSectionOfBillingTab.scss';

interface IInvoiceSectionOfBillingTab {
  orderId?: number;
  logisticServiceId?: number;
  storageInvoiceId?: number;
  companyName: string;
  emissionCountry: string;
  emissionCountryFlag?: string;
  billingCountry?: ICountryDetail;
  hasIVA: boolean;
  variant: TInvoiceSectionVariant;
  needInvoice?: boolean;
  billingProvider?: string;
  disabled?: boolean;
}

const InvoiceSectionOfBillingTab = ({
  orderId,
  logisticServiceId,
  storageInvoiceId,
  companyName,
  emissionCountry,
  emissionCountryFlag,
  billingCountry,
  hasIVA,
  variant,
  needInvoice,
  billingProvider,
  disabled
}: IInvoiceSectionOfBillingTab) => {
  const [invoiceRecords, setInvoiceRecords] = useState<(IInvoiceOrderRecord | IInvoiceLogisticServiceRecord)[]>([]);
  const isButtonAddInvoiceDisabled = invoiceRecords?.length >= 5 || invoiceRecords?.[0]?.id === undefined;

  // Determina si se necesita cargar una factura y si no hay registros de facturas o el último registro no tiene fecha de emisión ni número de factura
  const needInvoiceAndIsNotAutomatedBilling =
    needInvoice &&
    (!invoiceRecords ||
      invoiceRecords?.length === 0 ||
      (!invoiceRecords?.at(-1)?.invoiceEmissionDate && !invoiceRecords?.at(-1)?.invoiceNumber));

  // NOTA: Se obtiene el id de la orden o del servicio logístico para obtener las facturas asociadas a la orden o al servicio logístico. Si no hay facturas asociadas, se crea una factura vacía.
  const getInvoiceRecords = async (orderId?: number, logisticServiceId?: number) => {
    let currInvoiceRecords: IInvoiceRecord[] = [];
    try {
      if (variant === 'order' && orderId) {
        currInvoiceRecords = await getInvoiceOrderRecord(orderId);
        if (!currInvoiceRecords || currInvoiceRecords?.length === 0) {
          setInvoiceRecords([{ type: variant, orderId: orderId }]);
        }
      } else if (variant === 'logistic-service' && logisticServiceId) {
        currInvoiceRecords = await getInvoiceLogisticServiceRecord(logisticServiceId);
        if (!currInvoiceRecords || currInvoiceRecords?.length === 0) {
          setInvoiceRecords([{ type: variant, logisticServiceId: logisticServiceId }]);
        }
      }
      if (!currInvoiceRecords || currInvoiceRecords?.length === 0) return;
      setInvoiceRecords(currInvoiceRecords);
    } catch (error) {
      displayErrorNotification();
    }
  };

  const handleAddInvoice = () => {
    if (variant === 'order' && orderId) {
      setInvoiceRecords([...invoiceRecords, { type: variant, orderId: orderId }]);
    } else if (variant === 'logistic-service' && logisticServiceId) {
      setInvoiceRecords([...invoiceRecords, { type: variant, logisticServiceId: logisticServiceId }]);
    }
  };

  const updateCurrentInvoiceRecord = (
    invoiceRecord: IInvoiceOrderRecord | IInvoiceLogisticServiceRecord,
    index: number
  ) => {
    const updatedInvoiceRecords = [
      ...invoiceRecords.slice(0, index),
      invoiceRecord,
      ...invoiceRecords.slice(index + 1)
    ];
    setInvoiceRecords(updatedInvoiceRecords);
  };

  useEffect(() => {
    if (variant !== INVOICE_SECTION_VARIANT.STORAGE) {
      getInvoiceRecords(orderId, logisticServiceId);
    }
  }, [orderId, logisticServiceId, variant]); // eslint-disable-line react-hooks/exhaustive-deps

  if (variant === INVOICE_SECTION_VARIANT.STORAGE && storageInvoiceId) {
    return (
      <InvoiceSectionStorage
        storageInvoiceId={storageInvoiceId}
        companyName={companyName}
        emissionCountry={emissionCountry}
        emissionCountryFlag={emissionCountryFlag}
        billingCountry={billingCountry}
        hasIVA={hasIVA}
        billingProvider={billingProvider}
        disabled={disabled}
      />
    );
  }

  return (
    <section className={`invoiceSection ${needInvoiceAndIsNotAutomatedBilling ? 'warning' : ''}`}>
      <div className="invoiceSectionHeader">
        <p className="sectionTitle">Factura emitida por Bord</p>
        {needInvoiceAndIsNotAutomatedBilling && (
          <p className="warningText">
            <strong>Cargar factura</strong>{' '}
            <BordOneToneIcon
              variant="alertTriangleWarning"
              customWidth="12px"
              stroke={nudosRed100}
              standardSize={52}
              className="warningIcon"
            />
          </p>
        )}
      </div>
      <div className="titleSeparator">
        <hr className="separator" />
      </div>
      <section className="invoiceSectionCard">
        <BordInvoiceInfo
          beneficiary={companyName || ''}
          countryName={emissionCountry || ''}
          countryEmissionFlag={emissionCountryFlag || ''}
          billingCountry={billingCountry}
          isInvoicingWithIVA={hasIVA}
          billingProvider={billingProvider}
        />
        {invoiceRecords?.length > 0 &&
          invoiceRecords.map((invoiceRecord, index) => (
            <div key={`invoice-${variant}-${index}`} className="invoiceRowContainer">
              <InvoiceRow
                disabled={disabled}
                invoiceRecord={{ ...invoiceRecord, type: variant }}
                handleUpdateCurrentInvoiceRecord={invoiceRecord => updateCurrentInvoiceRecord(invoiceRecord, index)}
              />
              <hr className="separator" />
            </div>
          ))}
        <NudosButton
          buttonText="Adjuntar otra factura"
          colorPalette="secondary"
          buttonIcon={
            <BordOneToneIcon
              variant="plus"
              customWidth="12px"
              stroke={isButtonAddInvoiceDisabled ? nudosGray400 : fgDefault}
            />
          }
          customClassName="addInvoiceButton"
          handleClick={handleAddInvoice}
          isButtonDisabled={isButtonAddInvoiceDisabled}
        />
      </section>
    </section>
  );
};

export default InvoiceSectionOfBillingTab;
